import React, { useState } from "react";
import {
  FaAward,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaDownload,
  FaFilePdf,
  FaGlassCheers,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaShieldAlt,
  FaStar,
  FaUtensils,
  FaWhatsapp,
} from "react-icons/fa";
import { MdEventAvailable, MdGroups, MdLocalOffer } from "react-icons/md";
import BanquetAvailabilityCalendar from "./BanquetAvailabilityCalendar.jsx";
import BanquetEnquiryForm from "./BanquetEnquiryForm.jsx";
import BanquetGallery from "./BanquetGallery.jsx";
import BanquetPolicies from "./BanquetPolicies.jsx";
import BanquetSummary from "./BanquetSummary.jsx";
import { buildBanquetWhatsappLink, buildFoodTastingWhatsappLink, WHATSAPP_NUMBER } from "../../utils/banquetWhatsapp.js";
import { buildBanquetFaqs } from "../../utils/banquetFaq.js";
import { buildRenderableBanquetImages } from "../../utils/banquetImageUtils.js";
import { formatDistanceKm } from "../../utils/calculateDistance.js";

const currency = new Intl.NumberFormat("en-IN");
// Number(null) === 0, which passes Number.isFinite — so a plain Number.isFinite(Number(value))
// check never falls back for null and silently renders "0" instead of the intended fallback.
const formatNumber = (value, fallback = 0) =>
  currency.format(value === null || value === undefined || value === "" || !Number.isFinite(Number(value)) ? fallback : Number(value));
const price = (value, suffix = "") => (Number(value) > 0 ? `Rs. ${formatNumber(value)}${suffix}` : "");
const yesNo = (value) => (value ? "Allowed" : "Not allowed");
const list = (items) => (Array.isArray(items) ? items.filter(Boolean) : []);
const first = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");
const bookingAdvanceText = (value) => {
  if (typeof value === "string" && /\d+\s*%/.test(value)) {
    return `Reserve your date with ${value.match(/\d+\s*%/)[0].replace(/\s+/g, "")} advance`;
  }

  return value ? "Reserve your date with a small advance" : "Advance amount shared after availability check";
};
const addressText = (banquet) => first(
  banquet.locationDetails?.fullAddress,
  typeof banquet.address === "string" ? banquet.address : banquet.address?.line1,
  [banquet.locality, banquet.city].filter(Boolean).join(", ")
);
const baseSectionLinks = ["Availability", "Overview", "Pricing", "Capacity", "Amenities", "Food", "Decor", "Alcohol", "Timings", "Events", "Media", "Highlights", "FAQ", "Location", "Policies"];

function InfoCard({ icon, label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="min-w-0 rounded-[22px] border border-white/80 bg-white p-4 shadow-[0_18px_50px_-32px_rgba(15,23,42,0.65)] ring-1 ring-slate-100">
      <div className="flex items-start gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[#fff6df] text-[#9a6515] ring-1 ring-amber-100">{icon}</span>
        <div className="min-w-0">
          <p className="break-words text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 sm:tracking-[0.18em]">{label}</p>
          <p className="mt-1 break-words text-base font-bold leading-6 text-slate-950">{value}</p>
        </div>
      </div>
    </div>
  );
}

function Card({ id, eyebrow, title, reason, children }) {
  return (
    <section id={id} className="min-w-0 rounded-[24px] border border-slate-200/80 bg-white p-4 shadow-[0_24px_70px_-42px_rgba(15,23,42,0.55)] sm:rounded-[28px] sm:p-6">
      <p className="break-words text-xs font-bold uppercase tracking-[0.14em] text-[#9a6515] sm:tracking-[0.24em]">{eyebrow}</p>
      <div className="mt-2 flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
        <h2 className="break-words text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">{title}</h2>
        {reason ? <p className="max-w-xl text-sm font-medium leading-6 text-slate-600">{reason}</p> : null}
      </div>
      <div className="mt-5 min-w-0">{children}</div>
    </section>
  );
}

function Chips({ items, empty = "" }) {
  const safeItems = list(items);
  if (!safeItems.length) return empty ? <p className="text-sm text-slate-600">{empty}</p> : null;
  return (
    <div className="flex flex-wrap gap-2">
      {safeItems.map((item) => (
        <span key={item} className="inline-flex min-h-[40px] min-w-0 max-w-full items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold leading-5 text-slate-800 shadow-sm sm:rounded-full sm:px-4">
          <FaCheckCircle className="shrink-0 text-emerald-600" />
          <span className="min-w-0 break-words">{item}</span>
        </span>
      ))}
    </div>
  );
}

function DetailRows({ rows }) {
  const visibleRows = rows.filter(([, value]) => value || value === 0);
  if (!visibleRows.length) return null;
  return (
    <>
      {/* Mobile (<640px): dense divided list instead of a card per stat. */}
      <div className="divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 sm:hidden">
        {visibleRows.map(([label, value]) => (
          <div key={label} className="flex min-w-0 items-baseline justify-between gap-3 px-3 py-2">
            <p className="shrink-0 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500">{label}</p>
            <p className="min-w-0 break-words text-right text-xs font-bold leading-5 text-slate-950">{value}</p>
          </div>
        ))}
      </div>
      {/* Tablet/desktop (>=640px): unchanged boxed grid. */}
      <div className="hidden min-w-0 gap-3 sm:grid md:grid-cols-2">
        {visibleRows.map(([label, value]) => (
          <div key={label} className="min-w-0 rounded-[22px] border border-slate-200 bg-gradient-to-br from-white to-slate-50 px-4 py-4">
            <p className="break-words text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 sm:tracking-[0.16em]">{label}</p>
            <p className="mt-1 break-words text-sm font-bold leading-6 text-slate-950">{value}</p>
          </div>
        ))}
      </div>
    </>
  );
}

function PriceSpotlight({ pricing, banquet, whatsappHref }) {
  const vegPrice = price(pricing.vegStartingFrom ?? banquet.vegPrice, " / plate");
  const nonVegPrice = price(pricing.nonVegStartingFrom ?? banquet.nonVegPrice, " / plate");
  const rentalPrice = price(pricing.hallRentalStartingFrom || pricing.farmhouseRentalStartingFrom);
  const decorPrice = price(banquet.decorationServices?.decorationStartingPrice);
  const leadPrice = vegPrice || rentalPrice || decorPrice || "Check live offer";
  const minimumGuestRange = banquet.capacityDetails?.minimumGuestRange;
  const foodTastingAvailable = Boolean(banquet.foodCatering?.foodTastingAvailable);
  const foodTastingHref = foodTastingAvailable ? buildFoodTastingWhatsappLink({ banquet }) : "";

  return (
    <div className="min-w-0 overflow-hidden rounded-[24px] border border-amber-200 bg-white shadow-[0_30px_80px_-42px_rgba(120,53,15,0.75)] sm:rounded-[28px]">
      <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-[#3c2b12] p-5 text-white">
        <div className="inline-flex max-w-full items-center gap-2 rounded-full bg-amber-300 px-3 py-1 text-xs font-black uppercase tracking-[0.1em] text-slate-950 sm:tracking-[0.18em]">
          <MdLocalOffer />
          <span className="break-words">Best value check</span>
        </div>
        <p className="mt-5 text-sm font-semibold text-amber-100">Starts from</p>
        <p className="mt-1 break-words text-2xl font-black tracking-tight sm:text-3xl">{leadPrice}</p>
        <p className="mt-2 text-sm leading-6 text-slate-200">Get the latest date-wise offer, inclusions, and final quote instantly.</p>
      </div>
      <div className="grid gap-3 p-5">
        {minimumGuestRange ? (
          <div className="rounded-[22px] border border-emerald-200 bg-emerald-50 px-4 py-4 shadow-sm">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-emerald-800">Minimum Booking</p>
            <p className="mt-1 text-2xl font-black text-slate-950">{minimumGuestRange} Guests</p>
          </div>
        ) : null}
        <DetailRows rows={[
          ["Veg package", vegPrice],
          ["Non veg package", nonVegPrice || "Not available"],
          ["Hall rental", rentalPrice || "Check live offer"],
          ["Decoration", decorPrice || "Check live offer"],
          ["Booking advance", bookingAdvanceText(pricing.bookingAdvanceRequired)],
        ]} />
        <a href={whatsappHref} target="_blank" rel="noreferrer" className="inline-flex min-h-[50px] items-center justify-center gap-3 rounded-2xl bg-[#25D366] px-4 py-3 text-sm font-black text-white shadow-[0_20px_45px_-22px_rgba(37,211,102,0.85)]">
          <FaWhatsapp className="text-xl" />
          Check Live Offer
        </a>
        {foodTastingAvailable ? (
          <a href={foodTastingHref} target="_blank" rel="noreferrer" className="inline-flex min-h-[50px] items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-amber-400 to-[#b9852d] px-4 py-3 text-sm font-black text-slate-950 shadow-[0_20px_45px_-22px_rgba(185,133,45,0.85)]">
            <span aria-hidden="true">📅</span>
            Book Visit &amp; Food Tasting
          </a>
        ) : null}
      </div>
    </div>
  );
}

function TrustBadges({ banquet }) {
  const badges = [banquet.status === "published" ? [<FaShieldAlt />, "Verified Venue"] : null].filter(Boolean);

  if (!badges.length) return null;

  return (
    <div className="grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {badges.map(([icon, label]) => (
        <div key={label} className="flex min-w-0 items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/80 px-4 py-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-white text-emerald-700 shadow-sm">{icon}</span>
          <p className="min-w-0 break-words text-sm font-bold leading-5 text-slate-900">{label}</p>
        </div>
      ))}
    </div>
  );
}

function GoogleRatingHighlight({ banquet }) {
  const rating = Number.isFinite(banquet.googleRating) ? banquet.googleRating : null;
  const ratingsTotal = Number.isFinite(banquet.googleRatingsTotal) ? banquet.googleRatingsTotal : 0;

  if (!rating) return null;

  return (
    <div className="flex min-w-0 items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-white text-amber-500 shadow-sm">
        <FaStar />
      </span>
      <div className="min-w-0">
        <p className="text-lg font-black leading-6 text-slate-950">
          {rating} <span className="text-sm font-semibold text-slate-500">/ 5</span>
        </p>
        <p className="text-xs font-semibold text-slate-600">{formatNumber(ratingsTotal)} Google reviews</p>
      </div>
    </div>
  );
}

function FeatureIconGrid({ items }) {
  const visibleItems = items.filter((item) => item.value || item.value === 0);
  if (!visibleItems.length) return null;

  return (
    <div className="grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {visibleItems.map((item) => (
        <div key={item.label} className="min-w-0 rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-slate-950 text-amber-300">{item.icon}</span>
            <div className="min-w-0">
              <p className="break-words text-xs font-black uppercase tracking-[0.12em] text-slate-500 sm:tracking-[0.18em]">{item.label}</p>
              <p className="mt-1 break-words text-sm font-bold leading-6 text-slate-950">{item.value}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function HallCards({ halls }) {
  const safeHalls = list(halls).filter((hall) => hall?.hallName || hall?.seatingCapacity || hall?.floatingCapacity || hall?.hallCapacity);
  if (!safeHalls.length) return null;

  return (
    <div className="mt-5 grid min-w-0 gap-4 lg:grid-cols-2">
      {safeHalls.map((hall, index) => {
        const seated = hall.seatingCapacity;
        const floating = hall.floatingCapacity || hall.hallCapacity;
        const capacityLabel = [seated ? `${formatNumber(seated)} seated` : "", floating ? `${formatNumber(floating)} floating` : ""].filter(Boolean).join(" / ");
        return (
          <div key={`${hall.hallName || "Hall"}-${index}`} className="min-w-0 overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
            <div className="h-2 bg-gradient-to-r from-amber-300 via-[#b9852d] to-emerald-500" />
            <div className="p-4 sm:p-5">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#9a6515] sm:tracking-[0.2em]">Hall {index + 1}</p>
              <h3 className="mt-1.5 break-words text-lg font-black text-slate-950 sm:mt-2 sm:text-xl">{hall.hallName || "Celebration Hall"}</h3>
              <p className="mt-1 text-sm font-semibold text-slate-600">{hall.floor || "Floor details available"}</p>
              {/* Mobile (<640px): compact inline capacity instead of a full icon card. */}
              <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-2.5 py-1 text-xs font-bold text-slate-800 ring-1 ring-slate-200 sm:hidden">
                <MdGroups className="text-sm text-[#9a6515]" /> {capacityLabel || "Check live offer"}
              </p>
              {/* Tablet/desktop (>=640px): unchanged icon card. */}
              <div className="mt-4 hidden sm:block">
                <InfoCard icon={<MdGroups />} label="Guest Capacity" value={capacityLabel || "Check live offer"} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MediaTabs({ banquet, images }) {
  const [tab, setTab] = useState("Photos");
  const [failedImages, setFailedImages] = useState([]);
  const media = banquet.media || {};
  const videos = list(media.videoTeasers).map((item) => item.url).filter(Boolean);
  const droneVideos = list(media.droneVideos).map((item) => item.url).filter(Boolean);
  const tourLink = media.tour360?.link || "";
  const tabs = ["Photos", "Videos", "Drone Videos", "360 Tour"];
  const visiblePhotos = images.filter((image) => !failedImages.includes(image)).slice(0, 24);
  const markImageFailed = (image) => setFailedImages((current) => (current.includes(image) ? current : [...current, image]));
  return (
    <Card id="media" eyebrow="Photos & Videos" title="See the venue before you visit" reason="Photos, videos, drone footage, and tours make the venue easier to trust before shortlisting.">
      <div className="mb-5 flex flex-wrap gap-2">
        {tabs.map((item) => (
          <button key={item} type="button" onClick={() => setTab(item)} className={`min-h-[44px] rounded-full px-4 py-2 text-sm font-bold transition ${tab === item ? "bg-slate-950 text-white shadow-lg" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>{item}</button>
        ))}
      </div>
      {tab === "Photos" ? (
        visiblePhotos.length ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{visiblePhotos.map((image, index) => <img key={`${image}-${index}`} src={image} alt={`${banquet.name} gallery ${index + 1}`} className="aspect-[4/3] w-full rounded-[22px] object-cover shadow-sm" onError={() => markImageFailed(image)} />)}</div>
        ) : (
          <p className="text-sm text-slate-600">Photos are not available right now.</p>
        )
      ) : null}
      {tab === "Videos" ? <div className="grid gap-3 md:grid-cols-2">{videos.length ? videos.map((video) => <video key={video} src={video} controls className="aspect-video w-full rounded-2xl bg-slate-900" />) : <p className="text-sm text-slate-600">No teaser videos uploaded yet.</p>}</div> : null}
      {tab === "Drone Videos" ? <div className="grid gap-3 md:grid-cols-2">{droneVideos.length ? droneVideos.map((video) => <video key={video} src={video} controls className="aspect-video w-full rounded-2xl bg-slate-900" />) : <p className="text-sm text-slate-600">No drone videos uploaded yet.</p>}</div> : null}
      {tab === "360 Tour" ? (tourLink ? <a href={tourLink} target="_blank" rel="noreferrer" className="inline-flex min-h-[44px] items-center rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white">Open 360 Tour</a> : <p className="text-sm text-slate-600">360 tour not available yet.</p>) : null}
    </Card>
  );
}

const downloadFile = async (event, file) => {
  event.preventDefault();
  try {
    const response = await fetch(file.url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = file.fileName || `${file.label || "document"}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (downloadError) {
    // Cross-origin fetch can fail (network blip, CORS); falling back to a plain
    // tab-open still lets the visitor save the file manually — never a dead end.
    window.open(file.url, "_blank", "noopener,noreferrer");
  }
};

function PackagesMenuCard({ items }) {
  const files = list(items).filter((item) => item?.url);
  if (!files.length) return null;

  return (
    <Card id="packages" eyebrow="Packages & Menu" title="Pricing packages and food menu" reason="View or download the venue's packages and menu directly — no forms, no waiting.">
      <div className="grid min-w-0 gap-3 sm:grid-cols-2">
        {files.map((file, index) => (
          <div key={file.url || index} className="flex min-w-0 items-center gap-2.5 rounded-[22px] border border-slate-200 bg-white p-3 shadow-sm sm:gap-3 sm:p-4">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-rose-50 text-xl text-rose-600 sm:h-12 sm:w-12 sm:text-2xl"><FaFilePdf /></span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-slate-950">{file.label || file.fileName || "Document"}</p>
              {file.fileName ? <p className="truncate text-xs font-medium text-slate-500">{file.fileName}</p> : null}
            </div>
            <div className="flex shrink-0 gap-2">
              <a href={file.url} target="_blank" rel="noreferrer" className="inline-flex min-h-[40px] items-center justify-center rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50">View</a>
              <a href={file.url} onClick={(event) => downloadFile(event, file)} className="inline-flex min-h-[40px] items-center justify-center gap-1.5 rounded-xl bg-slate-950 px-3 py-2 text-xs font-bold text-white hover:bg-slate-800"><FaDownload /> Download</a>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function BanquetDetailExperience({
  banquet,
  distanceKm = null,
  selectedContext = null,
  onDateSelect = () => {},
  pageUrl = "",
  sourceLabel = "Verified Venue",
  previewMode = false,
}) {
  const media = banquet.media || {};
  const coverUrls = list(media.coverPhotos).map((item) => item.url).filter(Boolean);
  const galleryUrls = list(media.galleryPhotos).map((item) => item.url).filter(Boolean);
  const images = buildRenderableBanquetImages([...coverUrls, ...galleryUrls, ...(banquet.images || [])]);
  const location = banquet.locationDetails || {};
  const capacity = banquet.capacityDetails || {};
  const pricing = banquet.pricingDetails || {};
  const food = banquet.foodCatering || {};
  const decor = banquet.decorationServices || {};
  const alcohol = banquet.alcoholPolicy && typeof banquet.alcoholPolicy === "object" ? banquet.alcoholPolicy : {};
  const timing = banquet.timingDetails || {};
  const parking = banquet.parkingDetails || {};
  const availability = banquet.availabilityCalendar || {};
  const bookedSlots = availability.bookedSlots || banquet.bookedSlots || {};
  const eventTypes = list(banquet.eventTypes);
  const hasPackagesMenu = list(banquet.packagesMenu).some((item) => item?.url);
  const sectionLinks = hasPackagesMenu
    ? [...baseSectionLinks.slice(0, baseSectionLinks.indexOf("Pricing") + 1), "Packages", ...baseSectionLinks.slice(baseSectionLinks.indexOf("Pricing") + 1)]
    : baseSectionLinks;
  const faqs = buildBanquetFaqs(banquet);
  const mapUrl = location.googleMapLink || banquet.mapUrl || banquet.googleMapsUrl || "#";
  const whatsappHref = buildBanquetWhatsappLink({ banquet, selectedDate: selectedContext?.date, selectedSlot: selectedContext?.slotLabel, pageUrl });
  const venueTypes = list(banquet.basicDetails?.venueTypes).length ? banquet.basicDetails.venueTypes : [banquet.venueType].filter(Boolean);
  const highlightBadges = [
    banquet.acHall ? "AC Hall" : "",
    banquet.parkingAvailable ? "Parking" : "",
    capacity.floatingCapacity || banquet.guestMax ? `Capacity ${formatNumber(capacity.floatingCapacity || banquet.guestMax)}` : "",
    decor.inhouseDecoration ? "Decor Available" : "",
    capacity.bridalRoomAvailable ? "Bridal Room" : "",
  ].filter(Boolean);
  const priceRows = [
    ["Veg starting price", price(pricing.vegStartingFrom ?? banquet.vegPrice, " / plate")],
    ["Non veg starting price", price(pricing.nonVegStartingFrom ?? banquet.nonVegPrice, " / plate")],
    ["Hall rental starting", price(pricing.hallRentalStartingFrom)],
    ["Farmhouse rental starting", price(pricing.farmhouseRentalStartingFrom)],
    ["Decoration starting", price(decor.decorationStartingPrice)],
    ["Booking advance", bookingAdvanceText(pricing.bookingAdvanceRequired)],
    ["Parking charges", parking.parkingCharges],
  ];
  const todayKey = new Date().toISOString().split("T")[0];
  const todayStatus = availability.dates?.[todayKey] || "available";
  const availabilityLabel = availability.showToPublic === false ? "Hidden by venue" : todayStatus.replace(/([A-Z])/g, " $1");

  return (
    <div className={previewMode ? "min-w-0 space-y-7 rounded-[28px] bg-[#f6f3ed] p-3 sm:p-5" : "min-w-0 space-y-9"}>
      {previewMode ? <div className="rounded-2xl border border-emerald-100 bg-white px-4 py-3 text-sm font-semibold text-emerald-800">Manager preview: customer-facing listing layout.</div> : null}

      <section className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,1fr)_390px]">
        <div className="min-w-0 space-y-5">
          <BanquetGallery images={images} name={banquet.name} />
          <GoogleRatingHighlight banquet={banquet} />
          <TrustBadges banquet={banquet} />
        </div>
        <div className="xl:sticky xl:top-20 xl:self-start">
          <PriceSpotlight pricing={pricing} banquet={banquet} whatsappHref={whatsappHref} />
        </div>
      </section>

      {availability.showToPublic !== false ? (
        <section id="availability">
          <BanquetAvailabilityCalendar bookedSlots={bookedSlots} onSelectSlot={onDateSelect} selectedSlot={selectedContext} />
        </section>
      ) : null}

      <div className="sticky top-0 z-20 -mx-3 min-w-0 border-y border-slate-200 bg-white/95 px-3 py-2 shadow-sm backdrop-blur sm:mx-0 sm:rounded-2xl sm:border">
        <nav className="flex gap-2 overflow-x-auto" aria-label="Venue sections">
          {sectionLinks.map((label) => <a key={label} href={`#${label.toLowerCase().replaceAll(" ", "-")}`} className="min-h-[44px] shrink-0 whitespace-nowrap rounded-full px-4 py-3 text-sm font-bold text-slate-700 hover:bg-amber-50 hover:text-[#8a5a12]">{label}</a>)}
        </nav>
      </div>

      <section id="overview" className="grid min-w-0 gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-6">
          <BanquetSummary banquet={{ ...banquet, venueType: venueTypes[0] || banquet.venueType }} distanceKm={distanceKm} />
          <div className="min-w-0 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#9a6515] sm:tracking-[0.22em]">Why book here</p>
            <div className="mt-4 space-y-3">
              <Chips items={venueTypes} />
              <Chips items={highlightBadges} />
            </div>
          </div>
          <FeatureIconGrid items={[
            { icon: <FaAward />, label: "Premium fit", value: banquet.venueStyle || banquet.venueType || venueTypes[0] },
            { icon: <FaUtensils />, label: "Food planning", value: food.inhouseCatering ? "Inhouse catering available" : food.outsideCateringAllowed ? "Outside catering allowed" : "Catering details available on enquiry" },
            { icon: <FaGlassCheers />, label: "Celebration rules", value: Object.keys(alcohol).length ? yesNo(alcohol.alcoholAllowed) : banquet.liquorAllowed ? "Alcohol allowed" : "Alcohol policy on enquiry" },
            { icon: <FaClock />, label: "Event slots", value: timing.openingTime || timing.closingTime ? `${timing.openingTime || "-"} to ${timing.closingTime || "-"}` : availabilityLabel },
            { icon: <FaCalendarAlt />, label: "Visit planning", value: availability.showToPublic === false ? "Availability check ready" : "Date support available" },
            { icon: <FaShieldAlt />, label: "Trust layer", value: "Verified Venue" },
          ]} />
          {/* Mobile (<640px): dense inline stat row instead of 4 stacked icon cards. */}
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 rounded-2xl border border-slate-200 bg-white px-3 py-2 sm:hidden">
            <span className="inline-flex items-baseline gap-1 text-base font-black text-slate-950">
              {capacity.minimumGuestRange ? `${capacity.minimumGuestRange}` : `${formatNumber(banquet.guestMin, 50)}+`}
              <span className="text-[10px] font-medium text-slate-500">min booking</span>
            </span>
            <span aria-hidden="true" className="text-slate-300">&middot;</span>
            <span className="inline-flex items-baseline gap-1 text-base font-black text-slate-950">
              {formatNumber(capacity.seatingCapacity || banquet.guestMin)}/{formatNumber(capacity.floatingCapacity || banquet.guestMax)}
              <span className="text-[10px] font-medium text-slate-500">seated/floating</span>
            </span>
            <span aria-hidden="true" className="text-slate-300">&middot;</span>
            <span className="text-xs font-bold text-emerald-700">Book Site Visit</span>
          </div>
          {/* Tablet/desktop (>=640px): unchanged icon-card grid. */}
          <div className="hidden min-w-0 grid-cols-1 gap-3 sm:grid sm:grid-cols-2 xl:grid-cols-4">
            <InfoCard icon={<MdGroups />} label="Minimum Booking" value={capacity.minimumGuestRange ? `${capacity.minimumGuestRange} guests` : `${formatNumber(banquet.guestMin, 50)}+ guests`} />
            <InfoCard icon={<MdGroups />} label="Seating" value={capacity.seatingCapacity || banquet.guestMin ? `${formatNumber(capacity.seatingCapacity || banquet.guestMin)} guests` : ""} />
            <InfoCard icon={<MdGroups />} label="Floating" value={capacity.floatingCapacity || banquet.guestMax ? `${formatNumber(capacity.floatingCapacity || banquet.guestMax)} guests` : ""} />
            <InfoCard icon={<MdEventAvailable />} label="Visit Support" value="Book Site Visit" />
          </div>
        </div>

        <aside className="min-w-0 xl:sticky xl:top-20 xl:self-start">
          <div className="min-w-0 rounded-[28px] border border-slate-200 bg-white p-4 shadow-[0_24px_70px_-42px_rgba(15,23,42,0.55)] sm:p-5">
            <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-950">Book with confidence</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">Get latest price, availability, and visit support instantly.</p>
            <div className="mt-4 grid gap-2">
              <a href={`tel:+${WHATSAPP_NUMBER}`} className="inline-flex min-h-[48px] w-full items-center justify-center gap-3 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white"><FaPhoneAlt /> Call Now</a>
              <a href={whatsappHref} target="_blank" rel="noreferrer" className="inline-flex min-h-[48px] w-full items-center justify-center gap-3 rounded-2xl bg-[#25D366] px-4 py-3 text-sm font-semibold text-white"><FaWhatsapp className="text-xl" /> WhatsApp Now</a>
              <a href={whatsappHref} target="_blank" rel="noreferrer" className="inline-flex min-h-[48px] w-full items-center justify-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-[#85580f]">Book Site Visit</a>
              <a href={mapUrl} target="_blank" rel="noreferrer" className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700"><FaMapMarkerAlt className="text-emerald-600" /> Directions</a>
            </div>
          </div>
          <BanquetEnquiryForm banquet={banquet} />
        </aside>
      </section>

      <Card id="pricing" eyebrow="Pricing" title="Packages and charges" reason="Clear starting prices help you compare the venue before speaking to the team."><DetailRows rows={priceRows} /></Card>
      <PackagesMenuCard items={banquet.packagesMenu} />
      <Card id="capacity" eyebrow="Capacity & Structure" title="Venue layout" reason="Capacity, rooms, lifts, and parking decide whether the event feels spacious and smooth.">
        <DetailRows rows={[
          ["Seating capacity", capacity.seatingCapacity || banquet.guestMin],
          ["Floating capacity", capacity.floatingCapacity || banquet.guestMax],
          ["Minimum guest range", capacity.minimumGuestRange],
          ["Total Rooms in Banquet", capacity.roomsAvailable ? `${capacity.numberOfRooms || banquet.roomCount || 0} rooms` : "No"],
          ["Complimentary rooms", capacity.complimentaryRoomAvailable ? capacity.numberOfComplimentaryRooms || "Available" : "No"],
          ["Bridal rooms", capacity.bridalRoomAvailable ? capacity.numberOfBridalRooms || "Available" : "No"],
          ["Lift", capacity.liftAvailable ? `${capacity.passengerLiftCount || ""} passenger lift`.trim() : "No"],
          ["Wheelchair accessible", capacity.wheelchairAccessible ? "Yes" : "No"],
          ["Parking", parking.parkingAvailable || banquet.parkingAvailable ? `${parking.totalParkingCapacity || banquet.parkingCapacity || ""} ${list(parking.parkingTypes).join(", ")}`.trim() || "Available" : "No"],
          ["Valet parking", parking.valetParking ? "Available" : "No"],
        ]} />
        <HallCards halls={capacity.halls} />
      </Card>
      <Card id="amenities" eyebrow="Facilities" title="Amenities that improve guest comfort" reason="Facilities explain what guests will feel on the event day, not just what is listed."><Chips items={banquet.facilities} empty="Facilities will appear after the venue completes this section." /></Card>
      <Card id="food" eyebrow="Food & Catering" title="Catering options" reason="Food flexibility is one of the biggest booking decisions for weddings, birthdays, and family functions.">
        <DetailRows rows={[
          ["Inhouse catering", food.inhouseCatering ? "Yes" : "No"],
          ["Outside catering", food.outsideCateringAllowed ? "Allowed" : "Not allowed"],
          ["Kitchen", food.kitchenAvailable ? "Available" : "Not available"],
          ["Serving style", food.foodServingStyle],
          ["Live counters", list(food.liveCounters).join(", ")],
          ["Special food services", list(food.specialFoodServices).join(", ")],
        ]} />
      </Card>
      <Card id="decor" eyebrow="Decoration & Event Services" title="Decor and add-ons" reason="A good venue should support the event look, photo zones, themes, and production needs.">
        <div className="mb-5 rounded-[24px] border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-emerald-50 p-5 shadow-[0_24px_70px_-45px_rgba(120,53,15,0.65)]">
          <p className="text-sm font-black uppercase tracking-[0.16em] text-[#9a6515]">Decoration by DecorZoom Available</p>
          <p className="mt-2 text-base font-semibold leading-7 text-slate-800">Birthday, baby shower, anniversary and premium balloon setups can be planned with DecorZoom.</p>
        </div>
        <DetailRows rows={[
          ["Inhouse decoration", decor.inhouseDecoration ? "Yes" : "No"],
          ["Decoration team", decor.decorationTeam],
          ["Outside decorator", yesNo(decor.outsideDecoratorAllowed)],
          ["Decor packages", decor.decorPackagesAvailable ? "Available" : "Not listed"],
        ]} />
        {list(decor.decorationThemesAvailable).length || list(decor.decorationMaterialsAvailable).length || list(decor.additionalServicesAvailable).length ? (
          <div className="mt-6 space-y-5 border-t border-slate-100 pt-5">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-[#9a6515]">Decoration Options Available</p>
            {list(decor.decorationThemesAvailable).length ? (
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Themes</p>
                <Chips items={decor.decorationThemesAvailable} />
              </div>
            ) : null}
            {list(decor.decorationMaterialsAvailable).length ? (
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Materials</p>
                <Chips items={decor.decorationMaterialsAvailable} />
              </div>
            ) : null}
            {list(decor.additionalServicesAvailable).length ? (
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Additional Services</p>
                <Chips items={decor.additionalServicesAvailable} />
              </div>
            ) : null}
          </div>
        ) : null}
      </Card>
      {Object.keys(alcohol).length ? (
        <Card id="alcohol" eyebrow="Alcohol Policy" title="Alcohol details" reason="Alcohol rules, corkage, bar counters, and licensed service areas affect the final plan and budget.">
          {alcohol.alcoholAllowed && (alcohol.liquorLicenseAvailable || alcohol.corkageChargesApplicable) ? (
            <>
              {/* Mobile (<640px): compact pills instead of 2 stacked boxes. */}
              <div className="mb-5 flex min-w-0 flex-wrap gap-2 sm:hidden">
                {alcohol.liquorLicenseAvailable ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-800 ring-1 ring-emerald-200">Liquor License &middot; Licensed venue</span>
                ) : null}
                {alcohol.corkageChargesApplicable ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-[#9a6515] ring-1 ring-amber-200">Corkage &middot; {alcohol.corkageCharges || "Applicable"}</span>
                ) : null}
              </div>
              {/* Tablet/desktop (>=640px): unchanged boxed grid. */}
              <div className="mb-5 hidden min-w-0 gap-3 sm:grid sm:grid-cols-2">
                {alcohol.liquorLicenseAvailable ? (
                  <div className="rounded-[22px] border border-emerald-200 bg-emerald-50 px-4 py-4">
                    <p className="text-xs font-black uppercase tracking-[0.14em] text-emerald-800">Liquor License</p>
                    <p className="mt-1 text-sm font-bold text-slate-950">Licensed venue</p>
                  </div>
                ) : null}
                {alcohol.corkageChargesApplicable ? (
                  <div className="rounded-[22px] border border-amber-200 bg-amber-50 px-4 py-4">
                    <p className="text-xs font-black uppercase tracking-[0.14em] text-[#9a6515]">Corkage Charges Apply</p>
                    <p className="mt-1 text-sm font-bold text-slate-950">{alcohol.corkageCharges || "Applicable"}</p>
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
          <DetailRows rows={[
            ["Alcohol", yesNo(alcohol.alcoholAllowed)],
            ["Inhouse alcohol", alcohol.inhouseAlcoholAvailable ? "Available" : "No"],
            ["Outside alcohol", yesNo(alcohol.outsideAlcoholAllowed)],
            ["Liquor license", alcohol.liquorLicenseAvailable ? "Available" : "Not listed"],
            ["Corkage charges apply", alcohol.corkageChargesApplicable ? alcohol.corkageCharges || "Applicable" : "Not applicable"],
            ["Service areas", list(alcohol.alcoholServiceAreas).join(", ")],
            ["Bar", alcohol.barCounterAvailable ? alcohol.barType || "Available" : "No"],
            ["Bartender", alcohol.bartenderAvailable ? "Available" : "Not listed"],
          ]} />
        </Card>
      ) : null}
      <Card id="timings" eyebrow="Timings" title="Operating hours and event slots" reason="Slot flexibility decides whether the venue fits your rituals, lunch, dinner, or late-night plans.">
        <DetailRows rows={[
          ["Operating hours", timing.openingTime || timing.closingTime ? `${timing.openingTime || "-"} to ${timing.closingTime || "-"}` : ""],
          ["Events supported", list(timing.eventsSupported).join(", ")],
          ["Full day event", timing.fullDayEventAvailable ? "Available" : ""],
          ["Overnight event", timing.overnightEventAllowed ? "Allowed" : ""],
          ["Music / DJ limit", timing.musicDjTimingLimit],
        ]} />
      </Card>
      <Card id="events" eyebrow="Event Types" title="Events supported" reason="Book faster when the venue already handles your type of celebration."><Chips items={eventTypes} empty="Event support will appear after the venue completes this section." /></Card>
      <MediaTabs banquet={banquet} images={images} />
      <Card id="highlights" eyebrow="Venue Highlights" title="Why shortlist this venue" reason="These are the strongest reasons to compare this venue against similar listings."><Chips items={banquet.highlights} empty={sourceLabel} /></Card>
      <Card id="faq" eyebrow="Common Questions" title="Frequently asked questions" reason="Quick answers to what most customers ask before booking.">
        <div className="space-y-4">
          {faqs.map((faq) => (
            <div key={faq.q} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
              <p className="text-sm font-bold text-slate-900">{faq.q}</p>
              <p className="mt-1 text-sm leading-6 text-slate-600">{faq.a}</p>
            </div>
          ))}
        </div>
      </Card>
      <Card id="location" eyebrow="Location & Map" title={location.locality || banquet.locality || "Venue location"} reason="Location, route clarity, and distance make guest arrival easier.">
        <p className="text-sm leading-7 text-slate-600">{addressText(banquet) || "Address pending venue confirmation."}</p>
        {distanceKm !== null ? <p className="mt-3 text-sm font-semibold text-emerald-700">{formatDistanceKm(distanceKm)} from your selected pincode</p> : null}
        <a href={mapUrl} target="_blank" rel="noreferrer" className="mt-5 inline-flex min-h-[44px] items-center justify-center rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white">Get Directions</a>
      </Card>
      <section id="policies"><BanquetPolicies banquet={banquet} /></section>
    </div>
  );
}
